import Section from '@/components/common/Section';
import ProjectForm from '@/components/forms/ProjectForm';
import { motion } from 'framer-motion';
import { fadeUp, staggerContainer } from '@/animations/variants';

interface ContactMethod {
  title: string;
  description: string;
  icon: string;
  action: string;
  href?: string;
  onClick?: () => void;
}

const handleScheduleCall = (): void => {
  // Open Google Calendar booking page
  const bookingPageUrl = 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ2Tn6Hhto3E6w_Lc3RzjVLe9_4a7r6TyHakC1qAy8MfThNmP8I3Fxnk5rPu1jVjbbG3axqFYxiu';
  
  // Open the booking page in a new tab
  window.open(bookingPageUrl, '_blank', 'noopener,noreferrer');
};

const contactMethods: ContactMethod[] = [
  {
    title: "WhatsApp Assistant",
    description: "Connect with us instantly and lets get started with project requirements through WhatsApp.",
    icon: "💬",
    action: "Chat Now",
    href: "https://wa.me/919270161536?text=Hi%20Ennobridge%20Team!%20%F0%9F%91%8B%0A%0AI%20came%20across%20your%20website%20and%20I%27m%20interested%20in%20discussing%20a%20project.%20I%27d%20love%20to%20learn%20more%20about%20your%20services%20and%20how%20you%20can%20help%20with%20my%20requirements.%0A%0ALooking%20forward%20to%20connecting%20with%20you!",
  },
  {
    title: "Email",
    description: "Send us an email with your requirements and we'll respond within 24 hours with a comprehensive proposal.",
    icon: "📧",
    action: "Send Email",
    href: "mailto:connect@ennobridge.com",
  },
  {
    title: "Schedule Call",
    description: "Book a 1 Hour consultation call to discuss about your requirements and explore how we can collaborate.",
    icon: "📅",
    action: "Book Call",
    onClick: handleScheduleCall,
  },
];

const faqs = [
  {
    question: "How quickly can you get started on a project?",
    answer: "We can typically begin project work within 1-2 weeks of finalizing requirements and contracts. Our residential engineering teams allow for rapid mobilization."
  },
  {
    question: "Do you work with clients across different time zones?",
    answer: "Yes, our residential engineering model supports 24×7 availability across multiple time zones including India, Australia, Japan, US, and Europe."
  },
  {
    question: "What is your typical project engagement model?",
    answer: "We offer flexible engagement models including dedicated teams, project-based work, and consultant augmentation depending on your specific needs."
  },
  {
    question: "How do you ensure code quality and security?",
    answer: "We follow strict code review processes, automated testing, security best practices, and use tools like GitHub Copilot to enhance code quality and consistency."
  },
];

export default function ContactPage() {
  return (
    <>
      {/* Contact Intro */}

      {/* Contact Methods */}
      <Section
        title="How to Reach Us"
        subtitle="Choose the communication method that works best for you"
        centered
        background="surface"
      >
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={staggerContainer}
        >
          {contactMethods.map((method, index) => (
            <motion.div
              key={index}
              className="bg-background border border-border rounded-xl p-8 text-center hover:border-primary/50 transition-all duration-normal"
              variants={fadeUp}
              whileHover={{ y: -5 }}
            >
              <div className="text-6xl mb-4">{method.icon}</div>
              <h3 className="text-xl font-heading font-semibold text-textPrimary mb-4">
                {method.title}
              </h3>
              <p className="text-textSecondary mb-6 leading-relaxed">
                {method.description}
              </p>
              {method.onClick ? (
                <button
                  onClick={method.onClick}
                  className="inline-flex items-center justify-center w-full bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-heading font-semibold transition-all duration-normal"
                >
                  {method.action}
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
              ) : (
                <a
                  href={method.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-full bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-heading font-semibold transition-all duration-normal"
                >
                  {method.action}
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )}
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* Project Form */}
      <Section
        title="Project Requirements Form"
        subtitle="If details are clear to you, Tell us about what you are expecting and we'll get back to you with a detailed proposal"
        centered
      >
        <ProjectForm />
      </Section>

      {/* Fast Onboarding Message */}
      <Section centered background="surface">
        <motion.div 
          className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-8 md:p-12 text-center"
          variants={fadeUp}
        >
          <div className="text-6xl mb-6">⚡</div>
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-textPrimary mb-4">
            Fast Onboarding & Quick Response
          </h2>
          <p className="text-lg text-textSecondary max-w-3xl mx-auto mb-6">
            We understand that timing is crucial in software development. Our streamlined 
            onboarding process and dedicated residential teams mean we can start contributing 
            to your project quickly, often within days of our initial conversation.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="px-4 py-2 bg-primary/20 text-primary rounded-full">
              24-hour response time
            </span>
            <span className="px-4 py-2 bg-primary/20 text-primary rounded-full">
              1-2 weeks to project start
            </span>
            <span className="px-4 py-2 bg-primary/20 text-primary rounded-full">
              Rapid team scaling
            </span>
            <span className="px-4 py-2 bg-primary/20 text-primary rounded-full">
              Continuous communication
            </span>
          </div>
        </motion.div>
      </Section>

      {/* FAQ Section */}
      <Section
        title="Frequently Asked Questions"
        subtitle="Quick answers to common questions about working with us"
        centered
      >
        <motion.div 
          className="max-w-4xl mx-auto space-y-6"
          variants={staggerContainer}
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="bg-surface border border-border rounded-xl p-6"
              variants={fadeUp}
            >
              <h3 className="text-lg font-heading font-semibold text-textPrimary mb-3">
                {faq.question}
              </h3>
              <p className="text-textSecondary leading-relaxed">
                {faq.answer}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="text-center mt-12"
          variants={fadeUp}
        >
          <p className="text-textSecondary mb-4">
            Have more questions? We're here to help.
          </p>
          <button className="inline-flex items-center text-primary hover:text-primary/80 font-medium transition-colors">
            Ask a Question
            <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </button>
        </motion.div>
      </Section>
    </>
  );
}